import { useEffect, useRef } from "react"

/**
 * An A/B experiment arm rendered on the page that the inline editor can stamp
 * client-side. On production the public HTML ships **no** `data-experiment` /
 * `data-variant` attrs; instead each `<Experiment>` anchor registers its flag +
 * rendered variant here (in JS, never in the DOM), and `EditModeHost` injects
 * the stamps after a signed-in user activates edit mode. Mirrors the CMS
 * edit-region registry (`edit-region-registry.ts`) so the public build stays
 * byte-clean while internal users can still see which arm they're editing.
 * (AHD-7396)
 */
export interface ExperimentRegion {
  el: HTMLElement
  flag: string
  variant: string
}

const regions = new Set<ExperimentRegion>()
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((l) => l())
}

/** Register a live experiment-arm region. Returns an unregister function. */
export function registerExperimentRegion(
  el: HTMLElement,
  { flag, variant }: { flag: string; variant: string }
): () => void {
  const region: ExperimentRegion = { el, flag, variant }
  regions.add(region)
  notify()
  return () => {
    regions.delete(region)
    notify()
  }
}

/** Snapshot of the currently-registered experiment regions. */
export function listExperimentRegions(): ExperimentRegion[] {
  return Array.from(regions)
}

/** Subscribe to register/unregister changes. Returns an unsubscribe function. */
export function subscribeExperimentRegions(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Attach an experiment-arm region to a container element. Spread the returned
 * ref onto the `<Experiment>` anchor; the region registers on mount and
 * re-registers when the flag or rendered variant changes.
 */
export function useExperimentRegion<T extends HTMLElement = HTMLElement>(
  flag: string,
  variant: string
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    return registerExperimentRegion(el, { flag, variant })
  }, [flag, variant])
  return ref
}
